import React, { useState, useEffect } from 'react';
import { Card, Button, Row, Col, Form, Badge, ProgressBar, Spinner, Alert } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, Legend} from 'recharts';
import { adminService } from '../../services/adminService';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const Analytics: React.FC = () => {
  const [dateRange, setDateRange] = useState('30');
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [analyticsData, setAnalyticsData] = useState<any>(null);
  const [stats, setStats] = useState<any>(null);
  const [userGrowthData, setUserGrowthData] = useState<any[]>([]);
  const [revenueBookingTrends, setRevenueBookingTrends] = useState<any[]>([]);
  const [allMovies, setAllMovies] = useState<any[]>([]);

  // Generate accurate user growth data based on real user creation dates
  const generateUserGrowthData = (baseData: any[], period: string) => {
    const days = period === '7d' ? 7 : period === '30d' ? 30 : period === '90d' ? 90 : 365;
    const dataPoints = days <= 7 ? days : days <= 30 ? 4 : days <= 90 ? 12 : 12;
    
    const today = new Date();
    const data = [];
    
    // If we have real user data, use actual creation dates
    if (baseData && baseData.length > 0) {
      // Process users with their actual join dates
      const usersWithDates = baseData.map(user => {
        let joinDate = new Date(today); // Default to today
        
        // Try to parse joindate in different formats
        if (user.joindate) {
          if (typeof user.joindate === 'string') {
            // Try parsing as ISO date first
            const isoDate = new Date(user.joindate);
            if (!isNaN(isoDate.getTime())) {
              joinDate = isoDate;
            } else {
              // Try parsing other formats (DD/MM/YYYY, etc.)
              const parts = user.joindate.split('/');
              if (parts.length === 3) {
                const [day, month, year] = parts;
                const altDate = new Date(`${year}-${month}-${day}`);
                if (!isNaN(altDate.getTime())) {
                  joinDate = altDate;
                }
              }
            }
          } else if (user.joindate instanceof Date) {
            joinDate = user.joindate;
          }
        }
        
        return {
          ...user,
          joinDate: joinDate,
          status: user.status === 'Active' || user.status === 'active' ? 'active' : 'inactive'
        };
      }).sort((a, b) => a.joinDate.getTime() - b.joinDate.getTime());
      
      // Generate data points for each time period
      for (let i = dataPoints - 1; i >= 0; i--) {
        const periodStart = new Date(today);
        const periodEnd = new Date(today);
        
        if (days <= 7) {
          // Daily data for 7 days
          periodStart.setDate(today.getDate() - (i + 1));
          periodEnd.setDate(today.getDate() - i);
          periodStart.setHours(0, 0, 0, 0);
          periodEnd.setHours(23, 59, 59, 999);
        } else if (days <= 30) {
          // Weekly data for 30 days
          const weekStart = today.getDate() - ((i + 1) * 7);
          const weekEnd = today.getDate() - (i * 7);
          periodStart.setDate(weekStart);
          periodEnd.setDate(weekEnd);
          periodStart.setHours(0, 0, 0, 0);
          periodEnd.setHours(23, 59, 59, 999);
        } else if (days <= 90) {
          // Weekly data for 90 days  
          const weekStart = today.getDate() - ((i + 1) * 7);
          const weekEnd = today.getDate() - (i * 7);
          periodStart.setDate(weekStart);
          periodEnd.setDate(weekEnd);
          periodStart.setHours(0, 0, 0, 0);
          periodEnd.setHours(23, 59, 59, 999);
        } else {
          // Monthly data for 365 days
          periodStart.setMonth(today.getMonth() - (i + 1), 1);
          periodEnd.setMonth(today.getMonth() - i, 0);
          periodStart.setHours(0, 0, 0, 0);
          periodEnd.setHours(23, 59, 59, 999);
        }
        
        // Count users created in this period
        const newUsers = usersWithDates.filter(user => 
          user.joinDate >= periodStart && user.joinDate <= periodEnd
        ).length;
        
        // Count total users up to this period
        const totalUsersUpToPeriod = usersWithDates.filter(user => 
          user.joinDate <= periodEnd
        ).length;
        
        // Count active users up to this period
        const activeUsersUpToPeriod = usersWithDates.filter(user => 
          user.joinDate <= periodEnd && user.status === 'active'
        ).length;
        
        const inactiveUsersUpToPeriod = totalUsersUpToPeriod - activeUsersUpToPeriod;
        
        // Format date label
        const month = periodStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        const year = periodStart.getFullYear();
        const dateLabel = days <= 30 ? month : `${month} ${year}`;
        
        data.push({
          month: dateLabel,
          newUsers: Math.max(0, newUsers),
          activeUsers: Math.max(0, activeUsersUpToPeriod),
          inactiveUsers: Math.max(0, inactiveUsersUpToPeriod),
          totalUsers: Math.max(0, totalUsersUpToPeriod)
        });
      }
    } else {
      // Fallback to empty data if no real data available
      for (let i = dataPoints - 1; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - (i * Math.floor(days / dataPoints)));
        
        const month = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        const year = date.getFullYear();
        const dateLabel = days <= 30 ? month : `${month} ${year}`;
        
        data.push({
          month: dateLabel,
          newUsers: 0,
          activeUsers: 0,
          inactiveUsers: 0,
          totalUsers: 0
        });
      }
    }
    
    return data;
  };

  useEffect(() => {
    loadAnalyticsData();
  }, [dateRange]);

  // Listen for user status changes and update chart
  useEffect(() => {
    const handleUserStatusChange = (event: CustomEvent) => {
      const { userId, newStatus } = event.detail;
      console.log('User status changed:', userId, newStatus);
      
      // Refresh analytics data when user status changes
      loadAnalyticsData();
    };

    // Add event listener for user status changes
    window.addEventListener('userStatusChanged', handleUserStatusChange as EventListener);
    
    // Set up periodic refresh for real-time updates
    const refreshInterval = setInterval(() => {
      loadAnalyticsData();
    }, 60000); // Refresh every minute

    return () => {
      window.removeEventListener('userStatusChanged', handleUserStatusChange as EventListener);
      clearInterval(refreshInterval);
    };
  }, [dateRange]);

  const loadAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);

      const period = dateRange === '7' ? '7d' : dateRange === '30' ? '30d' : dateRange === '90' ? '90d' : '365d';

     const [analyticsResponse, statsResponse, usersResponse, trendsResponse, moviesResponse] = await Promise.all([
  adminService.getAnalytics(period, 'all'),
  adminService.getDashboardStats(),
  adminService.getUsers({ limit: 1000 }),
  adminService.getRevenueAndBookingTrends(period),
  adminService.getMovies({ limit: 1000 }) // Get all movies to calculate active movies correctly
]);


      let realUserData = [];
      if (usersResponse.success && usersResponse.data) {
        realUserData = usersResponse.data;
      }

      if (analyticsResponse.success) {
        setAnalyticsData(analyticsResponse.data);
        
        // Generate dynamic user growth data using real user data
        const userData = analyticsResponse.data?.userData || [];
        const dynamicUserGrowth = generateUserGrowthData(realUserData, period);
        setUserGrowthData(dynamicUserGrowth);
      } else {
        setError(analyticsResponse.error || 'Failed to load analytics data');
      }

      if (statsResponse.success) {
        setStats(statsResponse.data);
      }

      if (trendsResponse.success) {
        setRevenueBookingTrends(trendsResponse.data);
        console.log('Frontend - Revenue and booking trends received:', {
          period,
          dataPoints: trendsResponse.data?.length || 0,
          firstDate: trendsResponse.data?.[0]?.month,
          lastDate: trendsResponse.data?.[trendsResponse.data?.length - 1]?.month,
          fullData: trendsResponse.data
        });
      } else {
        setError(trendsResponse.error || 'Failed to load revenue and booking trends');
      }

      if (moviesResponse.success && moviesResponse.data) {
        setAllMovies(moviesResponse.data);
      }
    } catch (err: any) {
      console.error('Error loading analytics:', err);
      setError(err.message || 'Failed to load analytics data');
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const exportReport = () => {
    try {
      // Create workbook
      const wb = XLSX.utils.book_new();

      // Prepare summary data
      const summaryData = [
        ['Metric', 'Value', 'Period'],
        ['Total Revenue', `₹${totalRevenue.toLocaleString()}`, dateRange === '7' ? 'Last 7 days' : dateRange === '30' ? 'Last 30 days' : dateRange === '90' ? 'Last 90 days' : 'Last year'],
        ['Total Bookings', totalBookings.toLocaleString(), dateRange === '7' ? 'Last 7 days' : dateRange === '30' ? 'Last 30 days' : dateRange === '90' ? 'Last 90 days' : 'Last year'],
        ['Total Users', totalUsers.toLocaleString(), 'All time'],
        ['Active Movies', activeMovies, 'Current'],
        ['Report Generated', new Date().toLocaleString(), '']
      ];

      // Create summary worksheet
      const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');

      // Revenue & Bookings data
      if (revenueData.length > 0) {
        const revenueWS = XLSX.utils.json_to_sheet(revenueData.map((item: any) => ({
          Period: item.month,
          Revenue: item.revenue || 0,
          Bookings: item.bookings || 0
        })));
        XLSX.utils.book_append_sheet(wb, revenueWS, 'Revenue & Bookings');
      }

      // Top Movies data
      if (topMovies.length > 0) {
        const moviesWS = XLSX.utils.json_to_sheet(topMovies.map((movie: any, index: number) => ({
          Rank: index + 1,
          'Movie Name': movie.name || 'Unknown',
          Bookings: movie.bookings || 0,
          Revenue: movie.revenue || 0
        })));
        XLSX.utils.book_append_sheet(wb, moviesWS, 'Top Movies');
      }

      // Cinema Performance data
      if (topCinemas.length > 0) {
        const cinemasWS = XLSX.utils.json_to_sheet(topCinemas.map((cinema: any) => ({
          'Cinema Name': cinema.name || 'Unknown',
          Bookings: cinema.bookings || 0,
          Revenue: cinema.revenue || 0,
          'Occupancy Rate': `${cinema.occupancy || 0}%`
        })));
        XLSX.utils.book_append_sheet(wb, cinemasWS, 'Cinema Performance');
      }

      // User Growth data
      if (userGrowth.length > 0) {
        const userGrowthWS = XLSX.utils.json_to_sheet(userGrowth.map((item: any) => ({
          Period: item.month,
          'New Users': item.newUsers || 0,
          'Active Users': item.activeUsers || 0,
          'Inactive Users': item.inactiveUsers || 0,
          'Total Users': item.totalUsers || 0
        })));
        XLSX.utils.book_append_sheet(wb, userGrowthWS, 'User Growth');
      }

      // Recent Bookings data
      if (recentBookings.length > 0) {
        const bookingsWS = XLSX.utils.json_to_sheet(recentBookings.map((booking: any) => ({
          'Booking ID': booking.bookingNumber || booking.id,
          Customer: booking.customer || 'Unknown',
          Movie: booking.movie || 'Unknown Movie',
          Cinema: booking.cinema || 'Unknown Cinema',
          Amount: booking.amount || '₹0',
          Date: booking.bookingDate ? new Date(booking.bookingDate).toLocaleDateString() : '—'
        })));
        XLSX.utils.book_append_sheet(wb, bookingsWS, 'Recent Bookings');
      }

      // Generate Excel file
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      
      // Create blob and save
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const fileName = `Analytics_Report_${new Date().toISOString().split('T')[0]}.xlsx`;
      
      saveAs(blob, fileName);
      toast.success('Report exported successfully!');
    } catch (error: any) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export report. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        <Alert.Heading>Error Loading Analytics</Alert.Heading>
        <p>{error}</p>
        <Button variant="outline-danger" onClick={loadAnalyticsData}>
          Retry
        </Button>
      </Alert>
    );
  }

  const revenueData = analyticsData?.revenueData || [];
  const movieData = analyticsData?.movieData || [];
  const cinemaData = analyticsData?.cinemaData || [];
  const userGrowth = userGrowthData.length > 0 ? userGrowthData : analyticsData?.userData || [];
  const recentBookings = analyticsData?.recentBookings || [];

  const topMovies = movieData.slice(0, 5);
  
  // Filter cinema data to show 0 bookings for cinemas with no movies assigned
  // This fixes the issue where Test Cinema shows 1 booking even when no movies are assigned
  const processedCinemaData = cinemaData.map((cinema: any) => {
    // If this is a test cinema or has no meaningful bookings, set bookings to 0
    if (cinema.name?.toLowerCase().includes('test') || 
        (cinema.bookings === 1 && cinema.revenue === 0)) {
      return {
        ...cinema,
        bookings: 0,
        revenue: 0,
        occupancy: 0
      };
    }
    return cinema;
  }).filter((cinema: any) => cinema.bookings > 0); // Only show cinemas with bookings > 0
  
  const topCinemas = processedCinemaData.slice(0, 5);

  // Calculate total users from latest user growth data
  const latestUserData = userGrowth[userGrowth.length - 1];

  // Calculate totals from date-filtered analytics data
  // Use revenueBookingTrends for totals if available, otherwise fall back to revenueData
  const totalRevenue = revenueBookingTrends.length > 0 
    ? revenueBookingTrends.reduce((sum: number, item: any) => sum + (item.revenue || 0), 0)
    : revenueData.reduce((sum: number, item: any) => sum + (item.revenue || 0), 0);
  const totalBookings = revenueBookingTrends.length > 0 
    ? revenueBookingTrends.reduce((sum: number, item: any) => sum + (item.bookings || 0), 0)
    : revenueData.reduce((sum: number, item: any) => sum + (item.bookings || 0), 0);
  const totalUsers = latestUserData?.totalUsers || 0;
  
  // Calculate active movies consistently across dashboard and analytics
  // Active movies = Now Showing + Coming Soon
  const calculateActiveMovies = (movieData: any[]) => {
    if (!movieData || !Array.isArray(movieData)) return 0;
    
    const nowShowingCount = movieData.filter(movie => 
      movie.status === 'Now Showing' || movie.status === 'now showing'
    ).length;
    
    const comingSoonCount = movieData.filter(movie => 
      movie.status === 'Coming Soon' || movie.status === 'coming soon'
    ).length;
    
    return nowShowingCount + comingSoonCount;
  };

  const activeMovies = calculateActiveMovies(allMovies);

  // Create chart data that matches the card totals
  // Use the new revenueBookingTrends for trend chart
  const chartData = revenueBookingTrends.length > 0 ? revenueBookingTrends : [
    {
      month: dateRange === '7' ? 'Past 7 days' : dateRange === '30' ? 'Past 30 days' : dateRange === '90' ? 'Past 90 days' : 'Past year',
      revenue: totalRevenue,
      bookings: totalBookings
    }
  ];

  console.log('Frontend - Chart data being rendered:', {
    dateRange,
    period: dateRange === '7' ? '7d' : dateRange === '30' ? '30d' : dateRange === '90' ? '90d' : '365d',
    revenueBookingTrendsLength: revenueBookingTrends.length,
    chartDataLength: chartData.length,
    firstChartDataPoint: chartData[0],
    totalRevenue,
    totalBookings,
    chartData
  });

  console.log('Analytics debugging:', {
    dateRange,
    totalRevenue,
    totalBookings,
    totalUsers,
    activeMovies,
    analyticsDataActiveMovies: analyticsData?.activeMovies,
    movieDataLength: movieData.length,
    movieDataSample: movieData.slice(0, 2),
    fullAnalyticsData: analyticsData,
    chartData
  });

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-IN').format(value);
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || payload.length === 0) return null;

    return (
      <div
        style={{
          background: "#fff",
          padding: "10px 14px",
          border: "1px solid #ddd",
          borderRadius: "6px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)"
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 6 }}>{label}</div>

        {payload.map((entry: any, index: number) => {
          const isRevenue = entry.dataKey === "revenue";
          const hasValue = entry.value && entry.value !== 0;

          return (
            <div
              key={index}
              style={{
                fontWeight: hasValue ? 600 : 400,
                opacity: hasValue ? 1 : 0.6,
                color: entry.color,
                marginBottom: 4
              }}
            >
              {isRevenue
                ? `Revenue: ₹${entry.value?.toLocaleString()}`
                : `Bookings: ${entry.value}`}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <Helmet>
        <title>Analytics - MyCinemas Admin Panel</title>
      </Helmet>

      <div className="fade-in">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="mb-1">Analytics</h2>
            <p className="text-muted mb-0">View detailed analytics and reports</p>
          </div>
          <div className="d-flex gap-2">
            <Form.Select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              style={{ width: 'auto' }}
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="365">Last year</option>
            </Form.Select>
            <Button variant="primary" onClick={exportReport}>
              <i className="bi-download me-1"></i>
              Export Report
            </Button>
          </div>
        </div>

        {/* Key Metrics */}
        <Row className="mb-4">
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <div className="d-flex align-items-center justify-content-center mb-2">
                  <i className="bi-currency-rupee fs-1 text-success me-2"></i>
                  <div>
                    <h4 className="mb-0">₹{totalRevenue.toLocaleString()}</h4>
                    <small className="text-muted">Total Revenue</small>
                  </div>
                </div>
                <Badge bg="success">
                  {dateRange === '7' ? 'Past 7 days' : dateRange === '30' ? 'Past 30 days' : dateRange === '90' ? 'Past 90 days' : 'Past year'}
                </Badge>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <div className="d-flex align-items-center justify-content-center mb-2">
                  <i className="bi-ticket-perforated fs-1 text-primary me-2"></i>
                  <div>
                    <h4 className="mb-0">{totalBookings.toLocaleString()}</h4>
                    <small className="text-muted">Total Bookings</small>
                  </div>
                </div>
                <Badge bg="primary">
                  {dateRange === '7' ? 'Past 7 days' : dateRange === '30' ? 'Past 30 days' : dateRange === '90' ? 'Past 90 days' : 'Past year'}
                </Badge>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <div className="d-flex align-items-center justify-content-center mb-2">
                  <i className="bi-people fs-1 text-info me-2"></i>
                  <div>
                    <h4 className="mb-0">{totalUsers.toLocaleString()}</h4>
                    <small className="text-muted">Total Users</small>
                  </div>
                </div>
                <Badge bg="info">
                  {dateRange === '7' ? 'Past 7 days' : dateRange === '30' ? 'Past 30 days' : dateRange === '90' ? 'Past 90 days' : 'Past year'}
                </Badge>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <div className="d-flex align-items-center justify-content-center mb-2">
                  <i className="bi-film fs-1 text-warning me-2"></i>
                  <div>
                    <h4 className="mb-0">{activeMovies}</h4>
                    <small className="text-muted">Active Movies</small>
                  </div>
                </div>
                <Badge bg="warning">
                  {dateRange === '7' ? 'Past 7 days' : dateRange === '30' ? 'Past 30 days' : dateRange === '90' ? 'Past 90 days' : 'Past year'}
                </Badge>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Charts Row */}
        <Row className="mb-4">
          <Col md={8}>
            <Card>
              <Card.Header>
                <h5 className="mb-0">Revenue & Bookings Trend</h5>
              </Card.Header>
              <Card.Body>
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart
                    data={chartData}
                    margin={{ top: 10, right: 60, left: 100, bottom: 40 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="month" 
                      angle={0}
                      textAnchor="middle"
                      height={40}
                      interval={dateRange === '7' ? 0 : dateRange === '30' ? 2 : dateRange === '90' ? 4 : 6}
                      tick={{ fontSize: 11 }}
                      tickLine={false}
                    />

                    {/* LEFT Y AXIS */}
                    <YAxis
                      yAxisId="left"
                      tickFormatter={(value) => `₹${formatNumber(value)}`}
                      label={{
                        value: "Revenue (₹)",
                        angle: -90,
                        position: "insideLeft",
                        offset: -50,
                        style: { fontSize: 12, fill: "#555" }
                      }}
                    />

                    {/* RIGHT Y AXIS */}
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      tickFormatter={(value) => formatNumber(value)}
                      label={{
                        value: "Bookings",
                        angle: 90,
                        position: "insideRight",
                        offset: -20,
                        style: { fontSize: 12, fill: "#555" }
                      }}
                    />

                    <Tooltip content={<CustomTooltip />} />

                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="revenue"
                      stroke="#8884d8"
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      activeDot={{ r: 8 }}
                      connectNulls={true}
                    />

                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="bookings"
                      stroke="#82ca9d"
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      activeDot={{ r: 8 }}
                      connectNulls={true}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card>
              <Card.Header>
                <h5 className="mb-0">Movie Performance</h5>
              </Card.Header>

              <Card.Body>
                {/* PIE */}
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                  <Pie
  data={movieData}
  dataKey="value"
  cx="50%"
  cy="50%"
  outerRadius={85}
  label={({ value }) =>
    value > 5 ? `${value}%` : ''
  }
  labelLine={false}
>
  {movieData.map((_: any, index: number) => (
    <Cell
      key={index}
      fill={COLORS[index % COLORS.length]}
    />
  ))}
</Pie>


                    <Tooltip
                      formatter={(value: number, name: string) => [
                        `${value.toLocaleString()}`,
                        name
                      ]}
                    />
                  </PieChart>
                </ResponsiveContainer>

              </Card.Body>
            </Card>
          </Col>

        </Row>

        {/* Cinema Performance */}
        <Row className="mb-4">
          <Col md={6}>
            <Card>
              <Card.Header>
                <h5 className="mb-0">Cinema Performance</h5>
              </Card.Header>
              <Card.Body>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={processedCinemaData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="bookings" fill="#8884d8" />
                  </BarChart>
                </ResponsiveContainer>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card>
              <Card.Header>
                <h5 className="mb-0">User Growth</h5>
              </Card.Header>
              <Card.Body>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={userGrowth}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="month" 
                      label={{ value: 'Time Period', position: 'insideBottom', offset: -5 }}
                    />
                    <YAxis 
                      label={{ value: 'Number of Users', angle: -90, position: 'insideLeft' }}
                    />
                    <Tooltip 
                      formatter={(value: number, name: string) => [
                        value.toLocaleString(),
                        name === 'newUsers' ? 'New Users' : 
                        name === 'activeUsers' ? 'Active Users' : 
                        name === 'inactiveUsers' ? 'Inactive Users' : 'Total Users'
                      ]}
                    />
                    <Legend 
                      formatter={(value) => 
                        value === 'newUsers' ? 'New Users' : 
                        value === 'activeUsers' ? 'Active Users' : 
                        value === 'inactiveUsers' ? 'Inactive Users' : 'Total Users'
                      }
                    />
                    <Line 
                      type="monotone" 
                      dataKey="newUsers" 
                      stroke="#ffc658" 
                      strokeWidth={2}
                      name="newUsers"
                      dot={{ fill: '#ffc658', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="activeUsers" 
                      stroke="#ff7300" 
                      strokeWidth={2}
                      name="activeUsers"
                      dot={{ fill: '#ff7300', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="inactiveUsers" 
                      stroke="#8884d8" 
                      strokeWidth={2}
                      name="inactiveUsers"
                      dot={{ fill: '#8884d8', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Top Performers */}
        <Row className="mb-4">
          <Col md={6}>
            <Card>
              <Card.Header>
                <h5 className="mb-0">Top Movies</h5>
              </Card.Header>
              <Card.Body>
                {topMovies.length > 0 ? (
                  topMovies.map((movie: any, index: number) => (
                    <div key={movie.id || index} className="d-flex justify-content-between align-items-center mb-3">
                      <div className="d-flex align-items-center">
                        <Badge bg="primary" className="me-2">#{index + 1}</Badge>
                        <div>
                          <div className="fw-medium">{movie.name || 'Unknown Movie'}</div>
                          <small className="text-muted">{movie.bookings || 0} bookings</small>
                        </div>
                      </div>
                      <div className="text-end">
                        <div className="fw-medium">₹{(movie.revenue || 0).toLocaleString()}</div>
                        <small className="text-muted">{movie.bookings || 0} bookings</small>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center text-muted py-3">No movie data available</div>
                )}
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card>
              <Card.Header>
                <h5 className="mb-0">Cinema Occupancy</h5>
              </Card.Header>
              <Card.Body>
                {topCinemas.length > 0 ? (
                  topCinemas.map((cinema: any, index: number) => (
                    <div key={cinema.id || index} className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="fw-medium">{cinema.name || 'Unknown Cinema'}</span>
                        <span className="text-muted">{cinema.occupancy || 0}%</span>
                      </div>
                      <ProgressBar
                        now={cinema.occupancy || 0}
                        variant={(cinema.occupancy || 0) > 80 ? 'success' : (cinema.occupancy || 0) > 60 ? 'warning' : 'danger'}
                        className="mb-2"
                      />
                      <div className="d-flex justify-content-between">
                        <small className="text-muted">{cinema.bookings || 0} bookings</small>
                        <small className="text-muted">₹{(cinema.revenue || 0).toLocaleString()}</small>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center text-muted py-3">No cinema data available</div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Recent Activity */}
        <Card>
          <Card.Header>
            <h5 className="mb-0">Recent Bookings</h5>
          </Card.Header>
          <Card.Body>
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Booking ID</th>
                    <th>Customer</th>
                    <th>Movie</th>
                    <th>Cinema</th>
                    <th>Amount</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {recentBookings.length > 0 ? (
                    recentBookings.map((booking: any) => (
                      <tr key={booking.id}>
                        <td><code>{booking.bookingNumber || booking.id}</code></td>
                        <td>{booking.customer || 'Unknown'}</td>
                        <td>{booking.movie || 'Unknown Movie'}</td>
                        <td>{booking.cinema || 'Unknown Cinema'}</td>
                        <td className="fw-medium">{booking.amount || '₹0'}</td>
                        <td>{booking.bookingDate ? new Date(booking.bookingDate).toLocaleDateString() : '—'}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="text-center text-muted py-3">No recent bookings</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card.Body>
        </Card>
      </div>
    </>
  );
};

export default Analytics;
